import React, { useState, useCallback, useEffect, useRef, memo } from "react";

const DEBOUNCE_MS = 350;


export function OrangeCap({ height = 58, width: capWidth, children, className = "" }) {
    const W   = capWidth || 110;
    const H   = height;
    const mid = H / 2;
    const r   = mid;

    // Left edge is a semicircle arc bulging into the input's white area
    // (peaks at (r, mid)); right edge is the pill's rounded end cap.
    const orangePath = [
        `M 0 0`,
        `A ${r} ${r} 0 0 1 0 ${H}`,
        `L ${W - r} ${H}`,
        `A ${r} ${r} 0 0 0 ${W - r} 0`,
        `Z`,
    ].join(" ");

    return (
        <div
            className={`pill-cap ${className}`}
            style={{
                width: W,
                height: H,
                flexShrink: 0,
                marginLeft: -(r),
                position: "relative",
                zIndex: 1,
                clipPath: `path('${orangePath}')`,
                background: "linear-gradient(135deg, #f97316 0%, #ea580c 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                paddingLeft: r + 8,
                paddingRight: 8,
                gap: 10,
                boxSizing: "border-box",
            }}
        >
            {children}
        </div>
    );
}

/**
 * Manual location-based search pill: debounces free-text input and
 * reports the trimmed query (or null when cleared) to the parent.
 */
function NormalSearch({ onSearch }) {
    const [text,   setText]   = useState("");
    const timerRef = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => {
        clearTimeout(timerRef.current);
        const trimmed = text.trim();
        if (!trimmed) { onSearch?.(null); return; }
        timerRef.current = setTimeout(() => onSearch?.(trimmed), DEBOUNCE_MS);
        return () => clearTimeout(timerRef.current);
    }, [text, onSearch]);

    const handleSearch = useCallback(() => {
        const trimmed = text.trim();
        if (!trimmed) return;
        clearTimeout(timerRef.current);
        onSearch?.(trimmed);
    }, [text, onSearch]);

    const handleClear = useCallback(() => {
        setText("");
        onSearch?.(null);
        inputRef.current?.focus();
    }, [onSearch]);

    return (
        <div className="search-pill">

            <div className="pill-content">
                <div className="input-wrapper">
                    <input
                        ref={inputRef}
                        className="pill-input"
                        placeholder="Enter location to search manually"
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                        autoComplete="off"
                        aria-label="Search properties by location"
                    />
                    {text && (
                        <button className="clear-btn" onClick={handleClear}
                                aria-label="Clear" type="button">✕</button>
                    )}
                </div>
            </div>

            <OrangeCap height={58}>
                <button className="pill-btn" onClick={handleSearch}
                        type="button" aria-label="Search">
                    <svg width="26" height="26" viewBox="0 0 24 24" fill="none"
                         stroke="white" strokeWidth="2.3"
                         strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="11" cy="11" r="7.5" />
                        <line x1="16.5" y1="16.5" x2="22" y2="22" />
                    </svg>
                </button>
            </OrangeCap>

        </div>
    );
}

export default memo(NormalSearch);